import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import ActionsMenu from "./ActionsMenu";
import Swal from "sweetalert2";

export default function BookCollectionType() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    collection_type_name: "",
    delete_status: 0,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  // Fetch book collection types data (server-side pagination)
  const fetchData = async (page = 0, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/book-collection-type/${clientId}/${collegeId}?page=${
          page + 1
        }&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
      );
      if (data.success) {
        setRows((data.data.items || []).map((r) => ({ id: r.collection_type_id, ...r })));
        setRowCount(data.data.total || 0);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to load data",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData(page, pageSize, search);
    }
    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  const handleOpen = () => {
    setEditing(null);
    setForm({ collection_type_name: "", delete_status: 0 });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      collection_type_name: row.collection_type_name,
      delete_status: row.delete_status,
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure? 🧸",
      text: "This will hide the collection type, but you can always add a new one!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it! 💖",
      cancelButtonText: "No, keep it! 🥺",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/book-collection-type/${clientId}/${collegeId}/${row.collection_type_id}`
      );
      await Swal.fire("Deleted! 🗑️", "Your collection type has been deleted (softly)!", "success");
      fetchData();
    } catch (err) {
      await Swal.fire(
        "Oops! 😢",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/book-collection-type/${clientId}/${collegeId}/${editing.collection_type_id}`,
          {
            ...form,
            updated_by: user?.user_email || "system",
            updated_on: new Date().toISOString(),
          }
        );
        setSnackbar({ open: true, message: "Updated successfully", severity: "success" });
      } else {
        await axios.post(`${API_BASE_URL}/api/book-collection-type/${clientId}/${collegeId}`, {
          ...form,
          college_id: collegeId,
          created_by: user?.user_email || "system",
        });
        setSnackbar({ open: true, message: "Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.api.getRowIndex(params.id) + 1;
      },
    },
    { field: "collection_type_name", headerName: "Collection Type Name", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Book Collection Types
          </Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Collection Type
          </Button>
        </Toolbar>
        <Box height={400} mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
          />
        </Box>
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Collection Type" : "Add Collection Type"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Collection Type Name"
              value={form.collection_type_name}
              onChange={(e) => setForm({ ...form, collection_type_name: e.target.value })}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
